import React, {
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import './CommonTooltip.css';

interface CommonTooltipProps {
  content: string;
  show: boolean;
  x: number;
  y: number;
}

const CommonTooltip = forwardRef<HTMLDivElement, CommonTooltipProps>(
  ({ content, show, x, y }, ref) => {
    const [mounted, setMounted] = useState(false);
    const innerRef = useRef<HTMLDivElement | null>(null);
    const [pos, setPos] = useState({ left: x, top: y });

    useEffect(() => {
      setMounted(true);
      return () => setMounted(false);
    }, []);

    // Merge the forwarded ref with our internal ref so we can both expose the
    // node to callers and measure it ourselves.
    const setRefs = useCallback(
      (node: HTMLDivElement | null) => {
        innerRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }
      },
      [ref],
    );

    // After the tooltip renders, measure its real size and clamp the position
    // so it never overflows (and gets clipped) off any edge of the viewport.
    // Runs before paint, so the corrected position shows without flicker.
    useLayoutEffect(() => {
      const el = innerRef.current;
      if (!mounted || !show || !content || !el) return;

      const { width, height } = el.getBoundingClientRect();
      const scrollX =
        window.pageXOffset || document.documentElement.scrollLeft || 0;
      const scrollY =
        window.pageYOffset || document.documentElement.scrollTop || 0;
      const minLeft = scrollX;
      const minTop = scrollY;
      const maxLeft = scrollX + window.innerWidth - width;
      const maxTop = scrollY + window.innerHeight - height;

      const left = Math.min(Math.max(x, minLeft), Math.max(maxLeft, minLeft));
      const top = Math.min(Math.max(y, minTop), Math.max(maxTop, minTop));

      setPos((prev) =>
        prev.left === left && prev.top === top ? prev : { left, top },
      );
    }, [mounted, show, content, x, y]);

    if (!mounted || !show || !content) return null;

    const tooltipContent = (
      <div
        ref={setRefs}
        className="commonTooltip"
        style={{
          position: 'absolute',
          whiteSpace: 'normal',
          left: pos.left,
          top: pos.top,
          zIndex: 100001,
          pointerEvents: 'none',
        }}
      >
        {/* print as html content */}
        <div dangerouslySetInnerHTML={{ __html: content }} />
      </div>
    );

    return createPortal(tooltipContent, document.body);
  },
);

export default CommonTooltip;
